// Вкладка «Назначения»: где человек стоит сейчас и где стоял раньше, по какому тарифу.
//
// Ставка ПОКАЗЫВАЕТСЯ целым рублём, а хранится дробной (152,329749 при 85 000 на 558 часов).
// Показывать шесть знаков нельзя — человек начнёт сверять их с ведомостью и не сойдётся:
// начисление считается от исходной пары, а не от ставки. Поэтому рядом со ставкой всегда
// написано, из чего она получилась.
//
// Действия над назначением — перевод, закрытие, продление, изменение тарифа — стоят у самого
// назначения (правило 3), а не в шапке карточки: у человека их бывает несколько, и «перевести»
// без указания, какое именно, ничего не значит.
//
// Какие действия показывать, решает ДАТА ОКОНЧАНИЯ, а не «действует ли сегодня». Перевести и
// закрыть можно только бессрочное: у назначения с последним днём сервер отвечает на них
// «назначение уже закрыто», и до 19.10.2026 обе кнопки стояли и у действующего «по 31.10», то есть
// вели в гарантированный отказ. У назначения с последним днём действие одно — «Продлить»: 10.10.2026
// заказчик понял «По 30.09» как «период работы», и тридцать человек выпали из октября.
//
// «Продлить» тоже не ведёт в гарантированный отказ: его нет на закрытом объекте и снятом посту
// (вместо кнопки — строка, почему и что делать) и у уволенного, кому продлевать некуда. Правило
// одно с панелью продления (`../продление.ts`).

import {
  RiAddLine,
  RiArrowRightLine,
  RiCalendarScheduleLine,
  RiCloseCircleLine,
  RiCoinsLine,
  RiMapPin2Line,
  RiTimeLine,
} from '@remixicon/react';
import { useNavigate } from 'react-router';

import { countLabel } from '@/shared/text/plural';
import type { AssignmentOut, EmployeeOut } from '@/shared/api/endpoints/people';
import { useAuthStore } from '@/shared/auth/store';
import { formatRateRubles, formatRubles } from '@/shared/money/format';
import { can } from '@/shared/rbac/permissions';
import { formatHours } from '@/shared/hours/format';
import { formatDateOnly } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import { todayIso } from '@/shared/ui/date-field';
import * as Button from '@/shared/ui/button';
import { FieldList, FieldRow } from '@/shared/ui/field-row';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';

import type { ApiError } from '@/shared/errors/api-error';
import type { EmployeeMonthOut } from '@/shared/api/endpoints/timesheet';
import { НАЗВАНИЕ_ТИПА_СМЕНЫ } from '@/shared/api/endpoints/timesheet';
import { useEmployeeMonths } from '@/modules/payroll/queries';

import { rateModeTitle } from '../dictionaries';
import { закрытоМесто, уволенБезЗапаса } from '../продление';
import { useAssignmentsQuery } from '../queries';
import type { PeopleRoute } from '../route';
import {
  PANEL_ASSIGN,
  PANEL_CLOSE,
  PANEL_EXTEND,
  PANEL_RATE,
  PANEL_TRANSFER,
  openPanelHref,
} from '../route';

const EDIT_PERMISSION = 'core.employee.edit';
const RATE_PERMISSION = 'core.rate.edit';

export function AssignmentsTab({ employee, route }: { employee: EmployeeOut; route: PeopleRoute }) {
  const navigate = useNavigate();
  const query = useAssignmentsQuery(employee.id);
  const permissions = useAuthStore((state) => state.permissions);
  const canEdit = can(permissions, EDIT_PERMISSION);
  const canRate = can(permissions, RATE_PERMISSION);

  const addButton = (
    <Button.Root
      type='button'
      variant='primary'
      mode='stroke'
      size='xsmall'
      onClick={() => navigate(openPanelHref(route, PANEL_ASSIGN, employee.id))}
    >
      <Button.Icon as={RiAddLine} />
      Поставить на объект
    </Button.Root>
  );

  return (
    <div className='flex flex-col gap-4 p-5'>
      <ScreenState
        data={query.data}
        error={query.error}
        subject='назначения человека'
        isEmpty={(data) => data.items.length === 0}
        onRetry={() => void query.refetch()}
        loading={
          <div className='flex flex-col gap-3'>
            <Skeleton className='h-28 w-full' />
            <Skeleton className='h-28 w-full' />
          </div>
        }
        empty={
          <EmptyState
            icon={RiMapPin2Line}
            title='Человек не стоит ни на одном объекте'
            description='Назначений нет ни живых, ни закрытых: в табель человек не попадёт и в ведомость тоже. Поставьте человека на пост: там же задаётся тариф.'
            action={canEdit ? addButton : undefined}
          />
        }
      >
        {(data) => (
          <>
            {/* Правило 2: над списком подпись, ЧТО перечислено и сколько. Столбик карточек
                без числа сверху человек читает как «вот всё», не зная, всё ли доехало. */}
            <div className='flex items-center justify-between gap-3'>
              <h4 className='text-label-sm text-text-strong-950'>
                {countLabel(data.items.length, ['назначение', 'назначения', 'назначений'])}
              </h4>
              {canEdit && addButton}
            </div>
            <div className='flex flex-col gap-4'>
              {data.items.map((assignment) => (
                <AssignmentCard
                  key={assignment.id}
                  assignment={assignment}
                  employee={employee}
                  route={route}
                  canEdit={canEdit}
                  canRate={canRate}
                />
              ))}
            </div>
          </>
        )}
      </ScreenState>

      <ЧасыПоМесяцам employeeId={employee.id} />
    </div>
  );
}

/**
 * Сколько человек отработал: месяц, объект, часы, смены, невыходы.
 *
 * ## Почему здесь, а не отдельной вкладкой
 *
 * Вкладок в карточке восемь — это потолок правила 12, девятой не будет. Но место выбрано не
 * по остаточному принципу: тариф без часов и часы без тарифа по отдельности не отвечают ни на
 * один вопрос финансового директора. «Сколько ему платим» и «сколько он отработал» читаются
 * вместе или не читаются вовсе.
 *
 * ## Что было до
 *
 * Часы человека можно было увидеть ровно в двух местах: в сетке табеля объекта — то есть надо
 * заранее знать, где и когда он стоял, — и в расчётном листе за месяц, где ведомость уже
 * создана. Вопрос «сколько он у нас вообще отработал» ответа не имел, хотя данные для него
 * лежали в базе с первого дня: описание строки и её вид наружу были написаны ещё в августе и
 * не использовались ничем.
 */
function ЧасыПоМесяцам({ employeeId }: { employeeId: string }) {
  const query = useEmployeeMonths(employeeId);

  return (
    <div className='flex flex-col gap-3 border-t border-stroke-soft-200 pt-4'>
      <h4 className='text-label-sm text-text-strong-950'>Часы по месяцам</h4>
      <ScreenState
        data={query.data}
        error={query.error as ApiError | null}
        subject='часы человека'
        isEmpty={(data) => data.items.length === 0}
        onRetry={() => void query.refetch()}
        loading={<Skeleton className='h-20 w-full' />}
        empty={
          <EmptyState
            icon={RiTimeLine}
            title='Часов пока нет'
            description='В табеле этого человека ещё не отмечали. Часы появятся здесь, как только заполнят табель месяца по объекту.'
          />
        }
      >
        {(data) => (
          <FieldList>
            {data.items.map((месяц) => (
              <FieldRow
                key={`${месяц.year}-${месяц.month}-${месяц.site_id}`}
                label={месяц.period_title}
                value={`${formatHours(месяц.hours)} ч`}
                note={заметкаМесяца(месяц)}
              />
            ))}
          </FieldList>
        )}
      </ScreenState>
    </div>
  );
}

/**
 * Что писать под часами месяца.
 *
 * Объект называется ВСЕГДА: человека переводят внутри месяца, и строка «Август 2026 — 744 ч»
 * без объекта не отвечает на вопрос, где он эти часы отработал. Невыходы называются только
 * когда они есть: ноль невыходов это норма, и писать его в каждой строке значит утопить те
 * строки, где невыход правда был.
 */
function заметкаМесяца(месяц: EmployeeMonthOut): string {
  // Через `countLabel`, а не шаблоном: «31 смен» читается как поломка, и доверие теряет не
  // только эта строка, а весь блок с часами. Сторож грамматики чисел ловит ровно такое.
  const части = [месяц.site_name, countLabel(месяц.shifts, ['смена', 'смены', 'смен'])];
  if (месяц.absences > 0) {
    части.push(countLabel(месяц.absences, ['невыход', 'невыхода', 'невыходов']));
  }
  if (месяц.shift_type !== null) {
    части.push(НАЗВАНИЕ_ТИПА_СМЕНЫ[месяц.shift_type]);
  }
  return части.join(' · ');
}

function AssignmentCard({
  assignment,
  employee,
  route,
  canEdit,
  canRate,
}: {
  assignment: AssignmentOut;
  employee: EmployeeOut;
  route: PeopleRoute;
  canEdit: boolean;
  canRate: boolean;
}) {
  const navigate = useNavigate();
  const live = assignment.date_to === null || assignment.date_to >= todayIso();
  const сДатой = assignment.date_to !== null;
  // Где продлевать нельзя, кнопки нет: сервер откажет наверняка. Закрытое место называется
  // строкой — в карточке человека иначе не видно, почему «Продлить» пропало; уволенный виден и
  // так, по шапке карточки.
  const местоЗакрыто = сДатой ? закрытоМесто(assignment) : null;
  const продлить =
    canEdit && сДатой && местоЗакрыто === null && уволенБезЗапаса(assignment, employee) === null;
  const кнопки = продлить || (canEdit && !сДатой) || canRate;

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center gap-2'>
        <span className='truncate text-label-sm text-text-strong-950'>
          {assignment.site_name} · {assignment.post_name}
        </span>
        <Badge.Root variant='lighter' color={live ? 'green' : 'gray'} size='medium'>
          {live ? 'действует' : 'закрыто'}
        </Badge.Root>
      </div>

      <FieldList>
        <FieldRow
          label='Период'
          value={
            assignment.date_to === null
              ? `с ${formatDateOnly(assignment.date_from)}, без даты окончания`
              : `${formatDateOnly(assignment.date_from)} — ${formatDateOnly(assignment.date_to)}`
          }
          note={
            assignment.date_to === null
              ? 'назначение считается живым, пока у него нет последнего дня'
              : undefined
          }
        />
        <FieldRow
          label='Тариф'
          value={rateModeTitle(assignment.rate_mode)}
          note={rateOrigin(assignment)}
        />
        <FieldRow
          label='Ставка в час'
          value={
            assignment.hourly_rate === null ? null : `${formatRateRubles(assignment.hourly_rate)}`
          }
          missing='ставка не посчитана: не хватает исходных данных тарифа'
          note={rateNote(assignment)}
        />
      </FieldList>

      {canEdit && местоЗакрыто !== null && (
        <p className='text-paragraph-xs text-text-sub-600'>{местоЗакрыто}</p>
      )}

      {кнопки && (
        <div className='flex flex-wrap items-center gap-2'>
          {продлить && (
            <Button.Root
              type='button'
              variant='neutral'
              mode='stroke'
              size='xsmall'
              onClick={() => navigate(openPanelHref(route, PANEL_EXTEND, assignment.id))}
            >
              <Button.Icon as={RiCalendarScheduleLine} />
              Продлить
            </Button.Root>
          )}
          {canEdit && assignment.date_to === null && (
            <>
              <Button.Root
                type='button'
                variant='neutral'
                mode='stroke'
                size='xsmall'
                onClick={() => navigate(openPanelHref(route, PANEL_TRANSFER, assignment.id))}
              >
                <Button.Icon as={RiArrowRightLine} />
                Перевести
              </Button.Root>
              <Button.Root
                type='button'
                variant='neutral'
                mode='stroke'
                size='xsmall'
                onClick={() => navigate(openPanelHref(route, PANEL_CLOSE, assignment.id))}
              >
                <Button.Icon as={RiCloseCircleLine} />
                Закрыть
              </Button.Root>
            </>
          )}
          {canRate && (
            <Button.Root
              type='button'
              variant='neutral'
              mode='stroke'
              size='xsmall'
              onClick={() => navigate(openPanelHref(route, PANEL_RATE, assignment.id))}
            >
              <Button.Icon as={RiCoinsLine} />
              Изменить тариф
            </Button.Root>
          )}
        </div>
      )}
    </div>
  );
}

/** Из чего получилась ставка. Без этой строки её шесть знаков выглядят взятыми с потолка. */
function rateOrigin(assignment: AssignmentOut): string {
  if (assignment.rate_mode === 'by_period') {
    return `${formatRubles(assignment.period_amount)} за период при норме ${formatHours(
      assignment.norm_hours,
    )} часов`;
  }
  if (assignment.rate_mode === 'by_target') {
    return `целевая сумма ${formatRubles(assignment.target_amount)}`;
  }
  return 'ставка задана напрямую: объект с фиксированной ставкой';
}

/**
 * Почему «ставка × часы» может не совпасть с начислением. Финансовый директор проверяет ставку
 * умножением в уме: 152 × 558 = 84 816, а в ведомости 85 000, — и без этой строки читает
 * разницу как ошибку программы.
 *
 * До 23.09.2026 здесь стояло «начисление считается от исходной пары» — наши слова, а не его:
 * какой пары, из чего исходной, не сказано нигде на экране. И стояло у ВСЕХ назначений, хотя у
 * прямой ставки (124 назначения из 501 в копии боевой базы) никакой суммы за период нет, а
 * начисление там и есть «ставка × часы». Поэтому слова свои у каждого режима, а у прямой
 * ставки строки нет вовсе. Нет и у непосчитанной ставки: пояснять значение, которого нет,
 * нечего.
 */
function rateNote(assignment: AssignmentOut): string | undefined {
  if (assignment.hourly_rate === null) {
    return undefined;
  }
  if (assignment.rate_mode === 'by_period') {
    return 'округлена до рубля; начисление считается точно, от суммы за период и нормы часов, поэтому «ставка × часы» может с ним не совпасть';
  }
  if (assignment.rate_mode === 'by_target') {
    return 'округлена до рубля; начисление равно целевой сумме, поэтому «ставка × часы» может с ним не совпасть';
  }
  return undefined;
}
